// Edit Page dialog — opened from the "Edit Page" link in each Quick Edit Sitemap row.
// Manages a single page/post end-to-end in one place: title, content, parent/category, tags,
// meta title/description, publish status, canonical URL, JSON-LD schema, and an optional
// old→new redirect (which is created in the LSG redirects table, so it shows up in the
// Redirection Manager). One Save writes every changed field to WordPress.
//
// Content editing rule: a contenteditable WYSIWYG can only safely round-trip a CLASSIC page's
// post_content. For Gutenberg (block markup) and page builders (Elementor/Divi/Bricks/Beaver),
// editing the raw HTML would corrupt the blocks/layout — so those show an "Edit this page in
// {Builder}" button (opens the WP editor in a new tab) while every OTHER field stays editable.

import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { SitemapRow } from "./SitemapTab";
import type { WPClient, LsgRedirect } from "../lib/wpClient";
import { recordHistory } from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import type { SeoPlugin, PageBuilder } from "../types";
import { metaKeysFor, canonicalKeyFor } from "../lib/fixExecutor";
import { showConfirm, showPrompt } from "../lib/dialog";

interface Props {
  row: SitemapRow;
  getClient: () => WPClient | null;
  plugin: SeoPlugin;      // detected SEO plugin — routes meta/canonical writes
  builder: PageBuilder;   // site-wide builder label (fallback for the content-editor notice)
  siteUrl: string;        // for the "Edit in {Builder}" wp-admin link
  theme: "light" | "dark";
  onClose: () => void;
  onSaved: () => void;    // App refreshes the row, shows a toast, and closes the dialog
}

// How the left pane behaves for this page's content.
type ContentMode =
  | { kind: "classic"; content: string }
  | { kind: "builder"; label: string };

const BUILDER_LABEL: Record<PageBuilder, string> = {
  elementor: "Elementor",
  divi: "Divi",
  bricks: "Bricks",
  beaver: "Beaver Builder",
  gutenberg: "the WordPress Block Editor",
  unknown: "your page builder",
};

const pathOf = (u: string) => { try { return new URL(u).pathname.replace(/\/$/, "") || "/"; } catch { return u; } };
// Normalized (lowercase, trailing-slash-free) path key for matching a stored redirect source.
const srcKey = (u: string) => { try { return (new URL(u).pathname.replace(/\/+$/, "") || "/").toLowerCase(); } catch { return (u.replace(/[#?].*$/, "").replace(/\/+$/, "") || "/").toLowerCase(); } };

// Decide the content mode from the per-page builder model. Only a true Classic page (no
// Gutenberg block comments, no builder shortcodes) is edited in-dialog; everything else opens
// in WordPress so blocks/layouts are never flattened.
function decideContentMode(
  builderModel: { builder: "elementor" | "classic"; content: string | null },
  siteBuilder: PageBuilder,
): ContentMode {
  if (builderModel.builder === "elementor") return { kind: "builder", label: BUILDER_LABEL.elementor };
  const content = builderModel.content ?? "";
  if (content.includes("<!-- wp:")) return { kind: "builder", label: BUILDER_LABEL.gutenberg };
  if (/\[et_pb_/.test(content)) return { kind: "builder", label: "Divi" };
  if (/\[fl_builder/.test(content)) return { kind: "builder", label: "Beaver Builder" };
  if (/\[vc_row/.test(content)) return { kind: "builder", label: "WPBakery Page Builder" };
  // Empty post_content on a builder-only site means the content lives in the builder, not here.
  if (!content.trim() && (siteBuilder === "divi" || siteBuilder === "bricks" || siteBuilder === "beaver")) {
    return { kind: "builder", label: BUILDER_LABEL[siteBuilder] };
  }
  return { kind: "classic", content };
}

export function EditPageDialog({ row, getClient, plugin, builder, siteUrl, theme, onClose, onSaved }: Props) {
  const ep = row.type === "post" ? "posts" : "pages";
  const editorRef = useRef<HTMLDivElement | null>(null);
  const colorRange = useRef<Range | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // ── Editable field state ──
  const [title, setTitle] = useState(row.title);
  const [contentMode, setContentMode] = useState<ContentMode | null>(null);
  const initialContent = useRef<string>("");
  const [parentSel, setParentSel] = useState("");     // page: parent id as string ("0" = top). post: category id.
  const initialParent = useRef("");
  const [pageOpts, setPageOpts] = useState<Array<{ id: number; label: string }>>([]);
  const [catOpts, setCatOpts] = useState<Array<{ id: number; label: string }>>([]);
  const [tagsInput, setTagsInput] = useState(row.tags.join(", "));
  const initialTags = useRef(row.tags.join(", "));
  const [metaTitle, setMetaTitle] = useState(row.metaTitle);
  const [metaDesc, setMetaDesc] = useState(row.metaDescription);
  const [status, setStatus] = useState("publish");
  const initialStatus = useRef("publish");
  const [canonical, setCanonical] = useState("");
  const initialCanonical = useRef("");
  const [schemaText, setSchemaText] = useState("");
  const initialSchema = useRef("");
  const [redirectOn, setRedirectOn] = useState(false);
  const [redirectDest, setRedirectDest] = useState("");
  const [redirectStatus, setRedirectStatus] = useState(301);
  const existingRedirect = useRef<LsgRedirect | null>(null); // the page's current redirect, if any

  const canEditCanonical = plugin === "aioseo" || !!canonicalKeyFor(plugin); // false only for "unknown"

  // ── Load everything the dialog needs when it opens ──
  useEffect(() => {
    const client = getClient();
    if (!client) { setLoadErr("Connect your WordPress site first."); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const [model, statusMeta, schemaBlocks, inventory, cats, aioseo, redirects] = await Promise.all([
          client.getBuilderModel(ep, row.id),
          client.getPageStatusAndMeta(row.type, row.id),
          client.getAuditSchema(row.id, ep).catch(() => [] as string[]),
          row.type === "page" ? client.getAllPagesAndPosts().catch(() => []) : Promise.resolve([]),
          row.type === "post" ? client.getCategories().catch(() => []) : Promise.resolve([]),
          plugin === "aioseo" ? client.getAioseoMeta(row.id).catch(() => ({ title: "", description: "", canonical: "" })) : Promise.resolve(null),
          client.listRedirects().catch(() => [] as LsgRedirect[]),
        ]);
        if (!alive) return;

        // If this page already redirects somewhere, pre-check the box + prefill the destination so the
        // user edits (and we update) the existing redirect instead of creating a duplicate.
        const mine = redirects.find(rr => rr.enabled !== false && srcKey(rr.source) === srcKey(row.url)) ?? null;
        existingRedirect.current = mine;
        if (mine) { setRedirectOn(true); setRedirectDest(mine.destination); setRedirectStatus(mine.status || 301); }

        const mode = decideContentMode(model, builder);
        setContentMode(mode);
        initialContent.current = mode.kind === "classic" ? mode.content : "";

        // Status.
        setStatus(statusMeta.status);
        initialStatus.current = statusMeta.status;

        // Parent (pages) / category (posts).
        if (row.type === "page") {
          const opts = inventory
            .filter(p => p.type === "page" && p.id !== row.id)
            .map(p => ({ id: p.id, label: pathOf(p.url) || p.slug || String(p.id) }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
          setPageOpts(opts);
          const cur = String(statusMeta.parent || 0);
          setParentSel(cur); initialParent.current = cur;
        } else {
          const opts = cats
            .map(c => ({ id: c.id, label: c.name || c.slug || String(c.id) }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
          setCatOpts(opts);
          const cur = statusMeta.categories.length ? String(statusMeta.categories[0]) : "";
          setParentSel(cur); initialParent.current = cur;
        }

        // Canonical (AIOSEO from its own table; others from the registered post-meta key).
        let canon = "";
        if (plugin === "aioseo") canon = aioseo?.canonical ?? "";
        else {
          const key = canonicalKeyFor(plugin);
          if (key) { const v = statusMeta.meta[key]; canon = typeof v === "string" ? v : ""; }
        }
        setCanonical(canon); initialCanonical.current = canon;

        // Schema — present the stored JSON-LD blocks as one editable JSON array.
        const parsed: unknown[] = [];
        for (const b of schemaBlocks) { try { parsed.push(JSON.parse(b)); } catch { /* skip invalid */ } }
        const schemaStr = parsed.length ? JSON.stringify(parsed.length === 1 ? parsed[0] : parsed, null, 2) : "";
        setSchemaText(schemaStr); initialSchema.current = schemaStr;

        setLoading(false);
      } catch (e) {
        if (alive) { setLoadErr(e instanceof Error ? e.message : "Failed to load the page."); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed the WYSIWYG once, after loading resolves to Classic mode. Read the value back as the
  // baseline: the browser normalizes innerHTML on assignment, so comparing against the raw
  // source string would falsely flag an untouched page as "content changed".
  useEffect(() => {
    if (contentMode?.kind === "classic" && editorRef.current) {
      editorRef.current.innerHTML = contentMode.content;
      initialContent.current = editorRef.current.innerHTML;
    }
  }, [contentMode]);

  // ── WYSIWYG toolbar handlers (same rt-* system as KeywordEditorDialog) ──
  const exec = (cmd: string, value?: string) => { editorRef.current?.focus(); document.execCommand(cmd, false, value); };
  const onHeading = (e: React.ChangeEvent<HTMLSelectElement>) => { const s = e.currentTarget; exec("formatBlock", s.value === "P" ? "P" : s.value); s.value = "P"; };
  const onLink = async () => {
    const selBefore = window.getSelection();
    const savedRange = selBefore && selBefore.rangeCount ? selBefore.getRangeAt(0).cloneRange() : null;
    const url = await showPrompt("Enter the URL to link to:", { title: "Add Link", tone: "info", placeholder: "https://example.com/page/", defaultValue: "https://", confirmLabel: "Add Link" });
    if (!url) return;
    editorRef.current?.focus();
    if (savedRange) { const sel = window.getSelection(); sel?.removeAllRanges(); sel?.addRange(savedRange); }
    document.execCommand("createLink", false, url);
  };
  const onColorMouseDown = () => { const sel = window.getSelection(); colorRange.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null; };
  const onColorInput = (e: React.FormEvent<HTMLInputElement>) => {
    editorRef.current?.focus();
    if (colorRange.current) { const sel = window.getSelection(); sel?.removeAllRanges(); sel?.addRange(colorRange.current); }
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand("foreColor", false, (e.currentTarget as HTMLInputElement).value);
    document.execCommand("styleWithCSS", false, "false");
  };
  // Click a link in the editor to edit/remove its URL; Ctrl/Cmd+click opens it.
  const onEditorClick = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement).closest("a");
    if (!a || !a.getAttribute("href")) return;
    if (e.metaKey || e.ctrlKey) { e.preventDefault(); window.open(a.href, "_blank", "noopener,noreferrer"); return; }
    e.preventDefault();
    const current = a.getAttribute("href") ?? "";
    void (async () => {
      const url = await showPrompt("Edit the link URL (clear the field and save to remove the link, keeping its text):", { title: "Edit Link", tone: "info", placeholder: "https://example.com/page/", defaultValue: current, confirmLabel: "Save Link" });
      if (url === null) return;
      const trimmed = url.trim();
      if (!trimmed) a.replaceWith(document.createTextNode(a.textContent ?? ""));
      else a.setAttribute("href", trimmed);
    })();
  };

  const openInWp = () => window.open(`${siteUrl.replace(/\/$/, "")}/wp-admin/post.php?post=${row.id}&action=edit`, "_blank", "noopener,noreferrer");

  // ── Save ──
  async function handleSave() {
    const client = getClient();
    if (!client) { setSaveErr("Connect your WordPress site first."); return; }
    setSaveErr("");

    // Parse the schema textarea up front so a syntax error blocks the save cleanly.
    const schemaChanged = schemaText.trim() !== initialSchema.current.trim();
    let schemaBlocks: string[] | null = null;
    if (schemaChanged) {
      const t = schemaText.trim();
      if (!t) schemaBlocks = [];
      else {
        try {
          const obj = JSON.parse(t);
          schemaBlocks = (Array.isArray(obj) ? obj : [obj]).map(o => JSON.stringify(o));
        } catch {
          setSaveErr("Schema isn't valid JSON. Fix it (a single JSON-LD object, or an array of objects) or clear the box.");
          return;
        }
      }
    }

    // What's changing, for the confirm dialog.
    const contentChanged = contentMode?.kind === "classic" && editorRef.current != null && editorRef.current.innerHTML !== initialContent.current;
    const titleChanged = title.trim() !== row.title;
    const parentChanged = parentSel !== initialParent.current;
    const tagsChanged = tagsInput.trim() !== initialTags.current.trim();
    const metaTitleChanged = metaTitle.trim() !== row.metaTitle.trim();
    const metaDescChanged = metaDesc.trim() !== row.metaDescription.trim();
    const statusChanged = status !== initialStatus.current;
    const canonicalChanged = canonical.trim() !== initialCanonical.current.trim();
    // Redirect: add a new one, update the existing one when its destination/type changed, or remove
    // the existing one when the box is unchecked / the destination cleared.
    const destTrim = redirectDest.trim();
    const existing = existingRedirect.current;
    let redirectAction: "none" | "add" | "update" | "remove" = "none";
    if (redirectOn && destTrim) redirectAction = !existing ? "add" : ((destTrim !== existing.destination || redirectStatus !== existing.status) ? "update" : "none");
    else if (existing) redirectAction = "remove";

    const parts: string[] = [];
    if (titleChanged) parts.push("title");
    if (contentChanged) parts.push("content");
    if (parentChanged) parts.push(row.type === "page" ? "parent page" : "category");
    if (tagsChanged) parts.push("tags");
    if (metaTitleChanged) parts.push("meta title");
    if (metaDescChanged) parts.push("meta description");
    if (statusChanged) parts.push("status");
    if (canonicalChanged) parts.push("canonical URL");
    if (schemaChanged) parts.push("schema");
    if (redirectAction === "add") parts.push("a 301/302 redirect");
    else if (redirectAction === "update") parts.push("the page's redirect");
    else if (redirectAction === "remove") parts.push("remove the page's redirect");
    if (!parts.length) { onClose(); return; }

    const ok = await showConfirm(
      `Save changes to “${row.title || row.url}”: ${parts.join(", ")}.\n\nThis makes live changes to your WordPress site.`,
      { title: "Save Page Changes", tone: "danger", confirmLabel: "Save Changes" },
    );
    if (!ok) return;

    setSaving(true);
    const errors: string[] = [];

    // 1) Core post fields in one write (title / content / status / parent / categories).
    const body: Record<string, unknown> = {};
    if (titleChanged) body.title = title.trim();
    if (contentChanged && editorRef.current) body.content = editorRef.current.innerHTML;
    if (statusChanged) body.status = status;
    if (parentChanged) {
      if (row.type === "page") body.parent = Number(parentSel) || 0;
      else if (parentSel) body.categories = [Number(parentSel)];
    }
    if (tagsChanged) {
      const list = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
      try { body.tags = list.length ? await client.getOrCreateTags(list) : []; }
      catch (e) { errors.push(`tags: ${e instanceof Error ? e.message : "error"}`); }
    }
    if (Object.keys(body).length) {
      try { const r = await client.updatePageOrPost(row.type, row.id, body); if (!r.success) throw new Error(r.error ?? "update failed"); }
      catch (e) { errors.push(`page fields: ${e instanceof Error ? e.message : "error"}`); }
    }

    // 2) Meta title/description — routed by SEO plugin.
    if (metaTitleChanged || metaDescChanged) {
      try {
        if (plugin === "aioseo") {
          const f: { title?: string; description?: string } = {};
          if (metaTitleChanged) f.title = metaTitle.trim();
          if (metaDescChanged) f.description = metaDesc.trim();
          const r = await client.saveAioseoMeta(row.id, f);
          if (!r.success) throw new Error(r.error ?? "update failed");
        } else {
          const keys = metaKeysFor(plugin);
          if (!keys) throw new Error(`SEO meta isn't writable for the "${plugin}" plugin`);
          const metaBody: Record<string, string> = {};
          if (metaTitleChanged) metaBody[keys.title] = metaTitle.trim();
          if (metaDescChanged) metaBody[keys.description] = metaDesc.trim();
          const r = await client.updatePageOrPost(row.type, row.id, { meta: metaBody });
          if (!r.success) throw new Error(r.error ?? "update failed");
        }
      } catch (e) { errors.push(`meta: ${e instanceof Error ? e.message : "error"}`); }
    }

    // 3) Canonical URL — AIOSEO via its route, others via the registered post-meta key.
    if (canonicalChanged) {
      try {
        if (plugin === "aioseo") {
          const r = await client.saveAioseoMeta(row.id, { canonical: canonical.trim() });
          if (!r.success) throw new Error(r.error ?? "update failed");
        } else {
          const key = canonicalKeyFor(plugin);
          if (!key) throw new Error(`canonical isn't writable for the "${plugin}" plugin`);
          const r = await client.updatePageOrPost(row.type, row.id, { meta: { [key]: canonical.trim() } });
          if (!r.success) throw new Error(r.error ?? "update failed");
        }
      } catch (e) { errors.push(`canonical: ${e instanceof Error ? e.message : "error"}`); }
    }

    // 4) Schema (_audit_schema).
    if (schemaChanged && schemaBlocks) {
      try { const r = await client.setAuditSchema(row.id, schemaBlocks, ep); if (!r.success) throw new Error(r.error ?? "update failed"); }
      catch (e) { errors.push(`schema: ${e instanceof Error ? e.message : "error"}`); }
    }

    // 5) Redirect → LSG redirects table (also visible in the Redirection Manager). Add / update the
    // existing redirect when the destination changed, or remove it when cleared.
    if (redirectAction === "add") {
      try { const r = await client.createRedirect({ source: pathOf(row.url), destination: destTrim, status: redirectStatus }); if (!r.success) throw new Error(r.error ?? "create failed"); void recordHistory({ action: "redirect_added", url: `${pathOf(row.url)} → ${destTrim}`, siteKey: siteKey(row.url) }); }
      catch (e) { errors.push(`redirect: ${e instanceof Error ? e.message : "error"}`); }
    } else if (redirectAction === "update" && existing) {
      try { const r = await client.updateRedirect(existing.id, { destination: destTrim, status: redirectStatus, enabled: true }); if (!r.success) throw new Error(r.error ?? "update failed"); void recordHistory({ action: "redirect_added", url: `${pathOf(row.url)} → ${destTrim}`, siteKey: siteKey(row.url) }); }
      catch (e) { errors.push(`redirect: ${e instanceof Error ? e.message : "error"}`); }
    } else if (redirectAction === "remove" && existing) {
      try { const r = await client.deleteRedirect(existing.id); if (!r.success) throw new Error(r.error ?? "remove failed"); }
      catch (e) { errors.push(`redirect: ${e instanceof Error ? e.message : "error"}`); }
    }

    setSaving(false);
    if (errors.length) { setSaveErr(`Some changes weren't saved — ${errors.join("; ")}.`); return; }
    onSaved();
  }

  // ── Theme tokens (match SitemapBulkEditDialog / ParentPickerDialog) ──
  const lt = theme === "light";
  const panelBg = lt ? "#ffffff" : "#0f172a";
  const border = lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)";
  const textColor = lt ? "#0f172a" : "#e2e8f0";
  const labelColor = lt ? "#64748b" : "rgba(255,255,255,.6)";
  const inputBg = lt ? "#ffffff" : "rgba(255,255,255,.06)";
  const inputBorder = lt ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.15)";

  const label: CSSProperties = { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: labelColor, fontFamily: "'Inter','Mulish',sans-serif", marginBottom: 5 };
  const field: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 11px", background: inputBg, border: inputBorder, borderRadius: 6, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: textColor, outline: "none" };
  const optStyle: CSSProperties = { background: lt ? "#ffffff" : "#1e293b", color: lt ? "#0f172a" : "#e2e8f0" };
  const fieldGroup: CSSProperties = { marginBottom: 14 };

  const statusOptions = (() => {
    const base = [{ label: "Published", value: "publish" }, { label: "Draft", value: "draft" }];
    if (!base.some(o => o.value === initialStatus.current)) base.unshift({ label: initialStatus.current, value: initialStatus.current });
    return base;
  })();

  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 3200, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "4vh 16px" }}
      onMouseDown={() => { if (!saving) onClose(); }}
    >
      <div
        style={{ width: 1080, maxWidth: "100%", background: panelBg, border, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.45)", padding: 22, fontFamily: "'Inter','Mulish',sans-serif", display: "flex", flexDirection: "column", maxHeight: "92vh" }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: textColor }}>Edit Page</div>
          <div style={{ fontSize: 11, color: labelColor }}>{row.type === "post" ? "Post" : "Page"} #{row.id}</div>
        </div>
        <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#2563eb", marginBottom: 14, wordBreak: "break-all", textDecoration: "none" }}>{row.url}</a>

        {loading ? (
          <div style={{ padding: "48px 8px", textAlign: "center", color: labelColor, fontSize: 13 }}>Loading page…</div>
        ) : loadErr ? (
          <div style={{ padding: "40px 8px", textAlign: "center", color: "#dc2626", fontSize: 13 }}>{loadErr}</div>
        ) : (
          <div style={{ display: "flex", gap: 20, overflow: "hidden", flex: 1, minHeight: 0 }}>
            {/* ── Left: title bar + content ── */}
            <div style={{ flex: "1 1 56%", minWidth: 0, display: "flex", flexDirection: "column" }}>
              <div style={fieldGroup}>
                <label style={label}>Page Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)} style={{ ...field, fontSize: 15, fontWeight: 700 }} disabled={saving} />
              </div>

              {contentMode?.kind === "classic" ? (
                <>
                  <div className="rt-toolbar">
                    <select className="rt-tb-heading" title="Heading level" defaultValue="P" onChange={onHeading}>
                      <option value="P">Paragraph</option>
                      <option value="H1">H1</option><option value="H2">H2</option><option value="H3">H3</option>
                      <option value="H4">H4</option><option value="H5">H5</option><option value="H6">H6</option>
                    </select>
                    <button type="button" className="rt-tb-btn" title="Bold" onMouseDown={e => e.preventDefault()} onClick={() => exec("bold")}><strong>B</strong></button>
                    <button type="button" className="rt-tb-btn" title="Add link" onMouseDown={e => e.preventDefault()} onClick={() => void onLink()}>🔗 Link</button>
                    <button type="button" className="rt-tb-btn" title="Bulleted list" onMouseDown={e => e.preventDefault()} onClick={() => exec("insertUnorderedList")}>• Bullets</button>
                    <label className="rt-tb-color-wrap" title="Text color">
                      <input type="color" className="rt-tb-color" defaultValue="#1f2937" onMouseDown={onColorMouseDown} onInput={onColorInput} />
                      <span>A</span>
                    </label>
                    <button type="button" className="rt-tb-btn" title="Underline" onMouseDown={e => e.preventDefault()} onClick={() => exec("underline")}><span style={{ textDecoration: "underline" }}>U</span></button>
                    <button type="button" className="rt-tb-btn" title="Italic" onMouseDown={e => e.preventDefault()} onClick={() => exec("italic")}><em>I</em></button>
                    <button type="button" className="rt-tb-btn" title="Align left" onMouseDown={e => e.preventDefault()} onClick={() => exec("justifyLeft")}>≡</button>
                    <button type="button" className="rt-tb-btn" title="Align center" onMouseDown={e => e.preventDefault()} onClick={() => exec("justifyCenter")}>≣</button>
                  </div>
                  <div ref={editorRef} className="rt-editor" contentEditable suppressContentEditableWarning onClick={onEditorClick} style={{ flex: 1, minHeight: 320 }} />
                </>
              ) : (
                <div style={{ flex: 1, minHeight: 320, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center", background: lt ? "#f8fafc" : "rgba(255,255,255,.03)", border: inputBorder, borderRadius: 8, padding: 28 }}>
                  <div style={{ fontSize: 13.5, color: textColor, fontWeight: 600, lineHeight: 1.5, maxWidth: 380 }}>
                    This page was created with <strong>{contentMode?.label}</strong>.
                  </div>
                  <div style={{ fontSize: 12, color: labelColor, lineHeight: 1.5, maxWidth: 400 }}>
                    Its content can't be edited here without breaking the layout. Every other field on the right still saves from this dialog.
                  </div>
                  <button
                    type="button"
                    onClick={openInWp}
                    style={{ padding: "9px 18px", background: "linear-gradient(135deg,#2563eb,#3b82f6)", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
                  >Edit this page in {contentMode?.label} ↗</button>
                </div>
              )}
            </div>

            {/* ── Right: metadata fields ── */}
            <div style={{ flex: "1 1 44%", minWidth: 0, overflowY: "auto", paddingRight: 4 }}>
              <div style={fieldGroup}>
                <label style={label}>{row.type === "page" ? "Parent Page" : "Post Category"}</label>
                <select value={parentSel} onChange={e => setParentSel(e.target.value)} style={{ ...field, cursor: saving ? "not-allowed" : "pointer" }} disabled={saving}>
                  {row.type === "page" ? (
                    <>
                      <option value="0" style={optStyle}>Top level (no parent)</option>
                      {pageOpts.map(o => <option key={o.id} value={String(o.id)} style={optStyle}>{o.label}</option>)}
                    </>
                  ) : (
                    <>
                      <option value="" style={optStyle}>— None —</option>
                      {catOpts.map(o => <option key={o.id} value={String(o.id)} style={optStyle}>{o.label}</option>)}
                    </>
                  )}
                </select>
                {row.type === "page" && parentSel !== initialParent.current && (
                  <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>Changing the parent changes the URL — add a redirect below to 301 the old URL.</div>
                )}
              </div>

              <div style={fieldGroup}>
                <label style={label}>Page Tags (comma-separated)</label>
                <input value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="e.g. InnerPage, Service" style={field} disabled={saving} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Meta Title</label>
                <input value={metaTitle} onChange={e => setMetaTitle(e.target.value)} style={field} disabled={saving} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Meta Description</label>
                <textarea value={metaDesc} onChange={e => setMetaDesc(e.target.value)} rows={3} style={{ ...field, resize: "vertical", minHeight: 62 }} disabled={saving} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...field, cursor: saving ? "not-allowed" : "pointer" }} disabled={saving}>
                  {statusOptions.map(o => <option key={o.value} value={o.value} style={optStyle}>{o.label}</option>)}
                </select>
              </div>

              <div style={fieldGroup}>
                <label style={label}>Canonical URL</label>
                <input
                  value={canonical}
                  onChange={e => setCanonical(e.target.value)}
                  placeholder={canEditCanonical ? "https://example.com/canonical-page/" : ""}
                  style={{ ...field, opacity: canEditCanonical ? 1 : 0.6 }}
                  disabled={saving || !canEditCanonical}
                />
                <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>
                  {canEditCanonical
                    ? "Saved to your SEO plugin. Leave blank to use the default canonical."
                    : "No supported SEO plugin detected — canonical can't be set."}
                </div>
              </div>

              {/* Redirect toggle */}
              <div style={{ ...fieldGroup, border, borderRadius: 8, padding: "11px 12px", background: lt ? "#f8fafc" : "rgba(255,255,255,.03)" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", marginBottom: redirectOn ? 12 : 0 }}>
                  <input type="checkbox" checked={redirectOn} onChange={e => setRedirectOn(e.target.checked)} disabled={saving} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: textColor }}>Add a redirect for this page</span>
                </label>
                {redirectOn && (
                  <>
                    <div style={{ fontSize: 11, color: labelColor, marginBottom: 8 }}>Redirects <code style={{ fontFamily: "Roboto Mono, monospace" }}>{pathOf(row.url)}</code> → the destination. Added to the Redirection Manager.</div>
                    <label style={label}>Destination URL</label>
                    <input value={redirectDest} onChange={e => setRedirectDest(e.target.value)} placeholder="https://example.com/new-page/" style={{ ...field, marginBottom: 10 }} disabled={saving} />
                    <label style={label}>Redirect Type</label>
                    <select value={String(redirectStatus)} onChange={e => setRedirectStatus(Number(e.target.value))} style={{ ...field, cursor: "pointer" }} disabled={saving}>
                      <option value="301" style={optStyle}>301 — Permanent</option>
                      <option value="302" style={optStyle}>302 — Temporary</option>
                    </select>
                  </>
                )}
              </div>

              <div style={fieldGroup}>
                <label style={label}>Schema (JSON-LD)</label>
                <textarea
                  value={schemaText}
                  onChange={e => setSchemaText(e.target.value)}
                  rows={6}
                  placeholder="Schema added by the tool appears here. Paste a JSON-LD object (or an array of objects)."
                  style={{ ...field, resize: "vertical", minHeight: 110, fontFamily: "Roboto Mono, monospace", fontSize: 11.5, lineHeight: 1.5 }}
                  disabled={saving}
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
        )}

        {saveErr && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>{saveErr}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: "9px 16px", background: lt ? "#fff" : "transparent", color: lt ? "#475569" : "#e2e8f0", border: inputBorder, borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>Cancel</button>
          <button
            onClick={() => void handleSave()}
            disabled={saving || loading || !!loadErr}
            style={{ padding: "9px 18px", background: (saving || loading || !!loadErr) ? "#93c5a3" : "linear-gradient(135deg,#15803d,#16a34a)", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: (saving || loading || !!loadErr) ? "not-allowed" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
          >{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
